import React, { useContext, useEffect, useRef, useState } from 'react'
import { useRecaptchaV3 } from '../../../../hooks/useRecaptchaV3'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye'
import { faEyeSlash } from '@fortawesome/free-solid-svg-icons/faEyeSlash'
import {
  ActivityIndicator,
  Text,
  View,
  TextInput,
  Pressable,
  Linking,
  Platform,
} from 'react-native'

import { useTranslation } from 'react-i18next'
import { loginStyle } from '../../../../theme/global'
import { inputStyle } from '../../../../theme/inputs'
import { Formik } from 'formik'
import { buttonStyle } from '../../../../theme/buttons'
import { validationSchema } from './validation'
import { useLogin } from '../../hooks/useLogin'
import { textColor, whiteBackgroundColor } from '../../../../theme/colors'
import { AlertMessage } from '../../../../components/AlertMessage'
import { useUserMe } from '../../hooks/useUserMe'
import { AuthContext } from '../../../../context/Auth/AuthContext'
import { CheckBox } from '../../../../components/CheckBox'
import { Button } from '../../../../components/Button'
import { DOBRA } from '../../../../theme/fonts'

interface Props {
  initialEmail: string
  initialPassword: string
  rememberUser: boolean
}

export const LoginForm = ({ initialEmail, initialPassword, rememberUser }: Props) => {
  const { handleLogIn, getTokenUrl } = useContext(AuthContext)
  const { t } = useTranslation()
  const [hidePassword, sethidePassword] = useState(true)
  const [remerberUser, setRememberUser] = useState(rememberUser)
  const [isGettingToken, setIsGettingToken] = useState(false)
  const { token, tokenType, hasError, handleLogin, isLoading, setTokenObj } = useLogin()
  const { userInfo, hasError: msgError } = useUserMe(token)
  const { getToken: getRecaptchaToken, recaptchaElement } = useRecaptchaV3('login')
  const passRef = useRef('')

  const getToken = async () => {
    await Linking.getInitialURL().then((url) => {
      const splitToken = url ? url.split('?token=') : []
      if (url && splitToken.length > 1) {
        setTokenObj({
          token: splitToken[1],
          type: 'url',
        })
      }
    })
  }

  useEffect(() => {
    if (Platform.OS === 'web' && getTokenUrl) {
      getToken()
    }
  }, [])

  useEffect(() => {
    if (userInfo) {
      handleLogIn({
        token,
        tokenType,
        user: userInfo,
        pass: passRef.current,
        remember: remerberUser,
      })
    }
  }, [userInfo])

  return (
    <View style={loginStyle.cardContainer}>
      <Text style={loginStyle.cardTitle}>{t('ACCESO')}</Text>

      <View style={loginStyle.cardTitleLineSetting} />

      {(hasError.length > 0 || msgError.length > 0) && (
        <AlertMessage title='ERROR'>
          <Text style={{ textAlign: 'center', color: textColor }}>
            {hasError.length > 0 ? hasError : msgError.length > 0 ? msgError : ''}
          </Text>
        </AlertMessage>
      )}

      <Formik
        initialValues={{ email: initialEmail, password: initialPassword }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          setIsGettingToken(true)
          const recaptchaToken = await getRecaptchaToken().catch(() => '')
          setIsGettingToken(false)
          // eslint-disable-next-line camelcase
          handleLogin({ ...values, recaptcha_token: recaptchaToken })
          passRef.current = values.password
        }}
      >
        {({ handleChange, handleBlur, handleSubmit, values, errors, touched, isValid }) => (
          <View>
            <View style={{ marginBottom: 15 }}>
              <Text
                style={[
                  (values.email.length === 0 || errors.email?.length === 0) && inputStyle.label,
                  values.email.length > 0 && inputStyle.labelFloating,
                  'email' in errors && touched.email && inputStyle.labelError,
                  { fontFamily: DOBRA },
                ]}
              >
                {t('Usuario')}
              </Text>
              <TextInput
                onChangeText={handleChange('email')}
                onBlur={handleBlur('email')}
                value={values.email}
                style={[
                  !touched.email && inputStyle.basicInput,
                  !errors.email && touched.email && inputStyle.validInput,
                  'email' in errors && touched.email && inputStyle.invalidInput,
                ]}
              />
              {touched.email && errors.email && (
                <Text style={{ color: 'red' }}>{t(errors.email)}</Text>
              )}
            </View>

            <View>
              <Text
                style={[
                  (values.password.length === 0 || errors.password?.length === 0) &&
                    inputStyle.label,
                  values.password.length > 0 && inputStyle.labelFloating,
                  'password' in errors && touched.password && inputStyle.labelError,
                  { fontFamily: DOBRA },
                ]}
              >
                {t('Contraseña')}
              </Text>
              <TextInput
                secureTextEntry={hidePassword}
                onChangeText={handleChange('password')}
                onBlur={handleBlur('password')}
                value={values.password}
                style={[
                  !touched.password && inputStyle.basicInput,
                  !errors.password && touched.password && inputStyle.validInput,
                  'password' in errors && touched.password && inputStyle.invalidInput,
                ]}
              />
              {touched.password && errors.password && (
                <Text style={{ color: 'red' }}>{t(errors.password)}</Text>
              )}

              <Pressable
                style={inputStyle.icon}
                onPress={() => {
                  sethidePassword(!hidePassword)
                }}
              >
                <FontAwesomeIcon
                  icon={hidePassword ? faEyeSlash : faEye}
                  size={15}
                  color={'#C4C4C4'}
                />
              </Pressable>
            </View>

            <CheckBox
              isCheck={remerberUser}
              onPress={() => setRememberUser(!remerberUser)}
              item={{ id: 0, name: 'Recuérdame' }}
            />

            <View style={{ alignItems: 'center' }}>
              <Button
                label=''
                onPress={handleSubmit}
                disabled={isLoading || isGettingToken}
                customStyle={{
                  width: 150,
                  marginTop: 30,
                }}
              >
                {!isLoading && !isGettingToken && (
                  <Text style={buttonStyle.text}>{t('Iniciar Sesión')}</Text>
                )}
                {(isLoading || isGettingToken) && (
                  <ActivityIndicator size={30} color={whiteBackgroundColor} />
                )}
              </Button>
            </View>
          </View>
        )}
      </Formik>
      {recaptchaElement}
    </View>
  )
}
